// Pure-function filter for the MeetingFilterChipBar selections (cadence
// / status / minutes / dateRange / includeArchived) and the search box.
// AND-composes over a flat Meeting[] — grouping is a separate downstream
// pass. Status uses the DERIVED 5-state (so the synthetic 'overdue' /
// 'due-soon' chips match scheduled-but-past / scheduled-within-7d).
// Cadence treats children of recurring parents as recurring even though
// they themselves carry no recurrenceRule.
//
// Parent preservation: when a recurring parent's own row would be
// dropped by the predicate (parents are templates, so their derived
// status / minutes state is rarely meaningful in isolation), the parent
// is still kept whenever ANY of its children survive the predicate.
// Without this, downstream groupMeetings would orphan the children and
// render them as flat "One-off" rows because their parent is missing.

import { deriveMeetingStatus } from "./deriveMeetingStatus";
import type { MeetingFilterChipBarFilters } from "./MeetingFilterChipBar";
import type { Meeting } from "../../../../types";

const DAY_MS = 86_400_000;

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isRecurring(m: Meeting): boolean {
  return !!m.recurrenceRule || !!m.parentId;
}

function matchesCadence(
  m: Meeting,
  selected: MeetingFilterChipBarFilters["cadence"],
): boolean {
  if (selected.length === 0) return true;
  const recurring = isRecurring(m);
  for (const v of selected) {
    if (v === "recurring" && recurring) return true;
    if (v === "one-off" && !recurring) return true;
  }
  return false;
}

function matchesStatus(
  m: Meeting,
  selected: MeetingFilterChipBarFilters["status"],
  today: Date,
): boolean {
  if (selected.length === 0) return true;
  const derived = deriveMeetingStatus(m.status, m.date, today);
  return selected.includes(derived);
}

function matchesMinutes(
  m: Meeting,
  selected: MeetingFilterChipBarFilters["minutes"],
): boolean {
  if (selected.length === 0) return true;
  const isHeld = m.status === "held";
  const hasEvidence = (m.evidenceLinks ?? []).length > 0;
  for (const v of selected) {
    if (v === "with-minutes" && isHeld && hasEvidence) return true;
    if (v === "awaiting-minutes" && isHeld && !hasEvidence) return true;
    if (v === "not-applicable" && !isHeld) return true;
  }
  return false;
}

function matchesDateRange(
  m: Meeting,
  range: MeetingFilterChipBarFilters["dateRange"],
  today: Date,
): boolean {
  if (range === "all-time") return true;
  const day = isoToUTCDay(m.date);
  if (day === null) return false;
  const todayMs = startOfUTCDay(today);
  if (range === "this-week") {
    return day >= todayMs && day <= todayMs + 7 * DAY_MS;
  }
  if (range === "next-30-days") {
    return day > todayMs + 7 * DAY_MS && day <= todayMs + 30 * DAY_MS;
  }
  if (range === "past-30-days") {
    return day < todayMs && day >= todayMs - 30 * DAY_MS;
  }
  return true;
}

function matchesSearch(m: Meeting, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return m.name.toLowerCase().includes(q);
}

export function applyMeetingChipFilters(
  meetings: Meeting[],
  filters: MeetingFilterChipBarFilters,
  search: string,
  today: Date = new Date(),
): Meeting[] {
  const passes = (m: Meeting): boolean => {
    if (!filters.includeArchived && m.archivedAt) return false;
    if (!matchesCadence(m, filters.cadence)) return false;
    if (!matchesStatus(m, filters.status, today)) return false;
    if (!matchesMinutes(m, filters.minutes)) return false;
    if (!matchesDateRange(m, filters.dateRange, today)) return false;
    if (!matchesSearch(m, search)) return false;
    return true;
  };

  const surviving = meetings.filter(passes);

  // Preserve any recurring parent whose children survived but the
  // parent row itself didn't match the predicate. Without this,
  // groupMeetings would render the orphaned children as flat one-offs
  // and the user would lose the recurrence grouping the moment they
  // applied any chip / search.
  const survivingIds = new Set(surviving.map((m) => m.id));
  const parentIdsToRescue = new Set<string>();
  for (const m of surviving) {
    if (m.parentId && !survivingIds.has(m.parentId)) {
      parentIdsToRescue.add(m.parentId);
    }
  }
  if (parentIdsToRescue.size === 0) return surviving;

  const rescued: Meeting[] = [];
  for (const m of meetings) {
    if (parentIdsToRescue.has(m.id)) {
      // Honour includeArchived even when rescuing: an archived parent
      // stays hidden if the user hasn't opted in.
      if (!filters.includeArchived && m.archivedAt) continue;
      rescued.push(m);
    }
  }
  return [...rescued, ...surviving];
}
